import type {
  PulseAskRequest,
  PulseAskRoute,
  PulseAskRouteRequest,
  PulseCardStateUpdate,
  PulseCatalogStateUpdate,
  PulseCatalogView,
  PulseEmailRequest,
  PulseEmailResult,
  PulseExplainRequest,
  PulseExplanation,
  PulseLiveWork,
  PulseMemberFacts,
  PulseMemberSettings,
  PulseOrgFacts,
  PulseOrgPinCreate,
  PulseOrgPinMove,
  PulsePage,
  PulsePageExplainRequest,
  PulsePageRefresh,
  PulsePageSettingsUpdate,
  PulsePersona,
  PulsePreviewRequest,
  PulsePreviewResult,
  PulseQuestionView,
  PulseRefreshStart,
  PulseRegistryEntry,
  PulseSettingsView,
  PulseStudioExplainRequest,
  PulseSweepStart,
  PulseThemeEscalations
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/Pulse/Pulse.Controller.ts
// LINK apps/bot/src/API/Pulse/Pulse.Controller.ts

/**
 * Pulse's internal calls: the server's facts (ENG-3419) and the bot's sweep and marks (ENG-3426). Internal token only — never called from a browser.
 * The `staff*` calls and the page's calls (ENG-3221) are the exception: Studio calls the server with the member's own login.
 */
export class PulseAPI {
  static async getOrgFacts(orgId: number): Promise<PulseOrgFacts> {
    return APIService.Server.get<PulseOrgFacts>(`/pulse/internal/org/${orgId}/facts`);
  }

  static async getMemberFacts(orgId: number, memberId: number): Promise<PulseMemberFacts> {
    return APIService.Server.get<PulseMemberFacts>(`/pulse/internal/org/${orgId}/member/${memberId}/facts`);
  }

  /** The theme finder's week (ENG-3225): an org's escalations between two times, newest first. */
  static async getThemeEscalations(orgId: number, from: string, to: string): Promise<PulseThemeEscalations> {
    return APIService.Server.get<PulseThemeEscalations>(`/pulse/internal/org/${orgId}/escalations`, { from, to });
  }

  /** The bot's sweep found the member's emailed page due (ENG-3222): the server sends it, at most once a period. */
  static async emailPage(orgId: number, memberId: number, request: PulseEmailRequest): Promise<PulseEmailResult> {
    return APIService.Server.post<PulseEmailResult>(`/pulse/internal/org/${orgId}/member/${memberId}/email`, request);
  }

  /** Ask a question (ENG-3223): the bot's model picks one governed question and names its card, or says why none fits. */
  static async ask(request: PulseAskRouteRequest): Promise<PulseAskRoute> {
    return APIService.Bot.post<PulseAskRoute>(`/pulse/ask`, request);
  }

  /** The bot, after it writes a member's page: re-run their pinned questions (decision 21). */
  static async rerunQuestions(orgId: number, memberId: number): Promise<{ rerun: number }> {
    return APIService.Server.post<{ rerun: number }>(`/pulse/internal/org/${orgId}/member/${memberId}/questions/rerun`, {});
  }

  /** Every signal as the bot's code declares it (ENG-3222): what AI Studio's catalog lists. */
  static async registry(): Promise<PulseRegistryEntry[]> {
    return APIService.Bot.get<PulseRegistryEntry[]>(`/pulse/registry`);
  }

  /** bull's regular task (plan Decision 3): the bot answers at once and sweeps in the background. */
  static async sweep(): Promise<PulseSweepStart> {
    return APIService.Bot.post<PulseSweepStart>(`/pulse/sweep`, {});
  }

  /** Marks an org's Pulse members for changes that don't pass through the bot's broadcasts (review sync, persona set). */
  static async markOrg(orgId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>(`/pulse/marks/org/${orgId}`, {});
  }

  /** The staff preview — called by the server's staff-only proxy, never from a browser. */
  static async preview(orgId: number, memberId: number, persona: PulsePersona, promptId?: number): Promise<PulsePreviewResult> {
    const draft = promptId === undefined ? "" : `&promptId=${promptId}`;
    return APIService.Bot.post<PulsePreviewResult>(
      `/pulse/preview/org/${orgId}/member/${memberId}?persona=${encodeURIComponent(persona)}${draft}`,
      {}
    );
  }

  static async explain(request: PulseExplainRequest): Promise<PulseExplanation> {
    return APIService.Bot.post<PulseExplanation>(`/pulse/explain`, request);
  }

  /** A member's Refresh (ENG-3221 decision 1), from the server: the bot answers at once and writes the page in the background. */
  static async refresh(orgId: number, memberId: number): Promise<PulseRefreshStart> {
    return APIService.Bot.post<PulseRefreshStart>(`/pulse/refresh/org/${orgId}/member/${memberId}`, {});
  }

  /** The member's own page. */
  static async getPage(orgId: number): Promise<PulsePage> {
    return APIService.Server.get<PulsePage>(`/pulse/org/${orgId}/page`);
  }

  /** The member's live work; `fresh` skips the server's few-second share, for the recheck when a ring ends. */
  static async getLiveWork(orgId: number, fresh = false): Promise<PulseLiveWork> {
    return APIService.Server.get<PulseLiveWork>(`/pulse/org/${orgId}/page/live${fresh ? "?fresh=true" : ""}`);
  }

  static async refreshPage(orgId: number): Promise<PulsePageRefresh> {
    return APIService.Server.post<PulsePageRefresh>(`/pulse/org/${orgId}/page/refresh`, {});
  }

  /** Pin, unpin, widen, snooze, dismiss or reopen one card; answers with the page as it now stands. */
  static async setCardState(orgId: number, cardId: string, update: PulseCardStateUpdate): Promise<PulsePage> {
    return APIService.Server.put<PulsePage>(`/pulse/org/${orgId}/page/cards/${encodeURIComponent(cardId)}`, update);
  }

  /** The Pulse tab in account settings (ENG-3222): the emailed page, its hour and zone, and the freshness limit. */
  static async getPageSettings(orgId: number): Promise<PulseSettingsView> {
    return APIService.Server.get<PulseSettingsView>(`/pulse/org/${orgId}/page/settings`);
  }

  static async setPageSettings(orgId: number, update: PulsePageSettingsUpdate): Promise<PulseMemberSettings> {
    return APIService.Server.put<PulseMemberSettings>(`/pulse/org/${orgId}/page/settings`, update);
  }

  static async explainCard(orgId: number, request: PulsePageExplainRequest): Promise<PulseExplanation> {
    return APIService.Server.post<PulseExplanation>(`/pulse/org/${orgId}/page/explain`, request);
  }

  /** The open slot (ENG-3223): the member's question, answered or with why not. */
  static async askQuestion(orgId: number, request: PulseAskRequest): Promise<PulseQuestionView> {
    return APIService.Server.post<PulseQuestionView>(`/pulse/org/${orgId}/page/ask`, request);
  }

  static async pinQuestion(orgId: number, questionId: number): Promise<PulsePage> {
    return APIService.Server.put<PulsePage>(`/pulse/org/${orgId}/page/questions/${questionId}/pin`, {});
  }

  static async removeQuestion(orgId: number, questionId: number): Promise<PulsePage> {
    return APIService.Server.delete<PulsePage>(`/pulse/org/${orgId}/page/questions/${questionId}`);
  }

  /** View as (ENG-3434), Swivl staff only: a member's page, read-only; every view is logged. */
  static async staffPage(orgId: number, memberId: number): Promise<PulsePage> {
    return APIService.Server.get<PulsePage>(`/pulse/org/${orgId}/member/${memberId}/page`);
  }

  static async staffLiveWork(orgId: number, memberId: number, fresh = false): Promise<PulseLiveWork> {
    return APIService.Server.get<PulseLiveWork>(`/pulse/org/${orgId}/member/${memberId}/page/live${fresh ? "?fresh=true" : ""}`);
  }

  /** AI Studio > Testing > PULSE: the server's staff-only proxy to the preview. `promptId` previews that version of the page prompt. */
  static async staffPreview(orgId: number, memberId: number, persona: PulsePersona, promptId?: number): Promise<PulsePreviewResult> {
    const request: PulsePreviewRequest = promptId === undefined ? { persona } : { persona, promptId };
    return APIService.Server.post<PulsePreviewResult>(`/pulse/org/${orgId}/member/${memberId}/preview`, request);
  }

  /** AI Studio > Testing > PULSE: the server's staff-only proxy to Explain. */
  static async staffExplain(orgId: number, memberId: number, request: PulseStudioExplainRequest): Promise<PulseExplanation> {
    return APIService.Server.post<PulseExplanation>(`/pulse/org/${orgId}/member/${memberId}/explain`, request);
  }

  /** AI Studio > Prompts > Pulse > Signal catalog (ENG-3222), Swivl staff only. Every write answers with the catalog as it now stands. */
  static async staffCatalog(orgId: number): Promise<PulseCatalogView> {
    return APIService.Server.get<PulseCatalogView>(`/pulse/org/${orgId}/catalog`);
  }

  static async staffSetCatalogState(orgId: number, update: PulseCatalogStateUpdate): Promise<PulseCatalogView> {
    return APIService.Server.put<PulseCatalogView>(`/pulse/org/${orgId}/catalog/signals`, update);
  }

  static async staffAddOrgPin(orgId: number, create: PulseOrgPinCreate): Promise<PulseCatalogView> {
    return APIService.Server.post<PulseCatalogView>(`/pulse/org/${orgId}/catalog/pins`, create);
  }

  static async staffRemoveOrgPin(orgId: number, pinId: number): Promise<PulseCatalogView> {
    return APIService.Server.delete<PulseCatalogView>(`/pulse/org/${orgId}/catalog/pins/${pinId}`);
  }

  static async staffMoveOrgPin(orgId: number, pinId: number, move: PulseOrgPinMove): Promise<PulseCatalogView> {
    return APIService.Server.put<PulseCatalogView>(`/pulse/org/${orgId}/catalog/pins/${pinId}/position`, move);
  }
}
